import React, { useEffect, useRef, useState } from 'react';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';

/**
 * z-index for a modal that must always win over an ordinary in-app Modal (default 10000) --
 * e.g. ProTrialEndedModal/TeamUpdatedModal, which are mounted at the app root and are meant to
 * be non-dismissible/mandatory, but with no portal and no shared priority manager, an ordinary
 * case-level modal mounted later in the DOM would otherwise paint on top of (and capture clicks
 * ahead of) a "mandatory" one mounted earlier.
 */
export const ELEVATED_MODAL_Z_INDEX = 10500;

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    showCloseButton?: boolean;
    headerBackgroundColor?: string;
    /** When true, the content area expands to fill the modal body (use with a flex child). */
    contentFillsModal?: boolean;
    /** Override styles on the modal container (e.g. maxWidth, overflow). */
    modalStyle?: React.CSSProperties;
    /**
     * Overlay z-index. Every Modal instance shares the same default (10000) and none of them
     * are portaled, so which one paints on top when two are open is otherwise pure DOM/mount
     * order -- e.g. a case-level modal (mounted inside <Routes>) can bury a mandatory app-root
     * modal (mounted before <Routes>, e.g. ProTrialEndedModal/TeamUpdatedModal) that's supposed
     * to always win. Callers with a genuine priority requirement should pass an explicit value
     * above 10000 rather than relying on mount order.
     */
    zIndex?: number;
}

/**
 * Modal component for displaying overlays and dialogs
 */
export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    showCloseButton = true,
    headerBackgroundColor,
    contentFillsModal = false,
    modalStyle,
    zIndex = 10000,
}) => {
    const theme = useTheme();
    const mouseDownOutsideRef = useRef<boolean>(false);

    // Handle escape key
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const modalStyles = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex,
            padding: theme.spacing.lg,
        },
        modal: {
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            ...(contentFillsModal ? { display: 'flex' as const, flexDirection: 'column' as const, minHeight: '40vh' } : {}),
            ...modalStyle,
        },
        header: {
            padding: theme.spacing.xl,
            borderBottom: `1px solid ${theme.colors.border.default}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: headerBackgroundColor ?? theme.colors.primary,
        },
        title: {
            fontSize: theme.typography.fontSize.xl,
            fontWeight: theme.typography.fontWeight.bold,
            color: theme.colors.text.inverse,
            margin: 0,
        },
        closeButton: {
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: theme.colors.text.inverse,
            padding: '4px',
            borderRadius: theme.borderRadius.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
        },
        content: {
            padding: theme.spacing.xl,
            ...(contentFillsModal ? { flex: 1, minHeight: 0, display: 'flex' as const, flexDirection: 'column' as const } : {}),
        },
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        // Track if mousedown happened on the overlay (outside modal content)
        if (e.target === e.currentTarget) {
            mouseDownOutsideRef.current = true;
        } else {
            mouseDownOutsideRef.current = false;
        }
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        // Only close if both mousedown and mouseup happened outside the modal
        if (e.target === e.currentTarget && mouseDownOutsideRef.current) {
            onClose();
        }
        // Reset the flag
        mouseDownOutsideRef.current = false;
    };

    return (
        <div 
            style={modalStyles.overlay} 
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
        >
            <div style={modalStyles.modal} onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()}>
                <div style={modalStyles.header}>
                    <h2 style={modalStyles.title}>{title}</h2>
                    {showCloseButton && (
                        <button
                            style={modalStyles.closeButton}
                            onClick={onClose}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.background}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            ×
                        </button>
                    )}
                </div>
                <div style={modalStyles.content}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void | Promise<void>;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    confirmButtonStyle?: 'primary' | 'danger';
    /** Extra content under the message, e.g. an optional note field. */
    children?: React.ReactNode;
}

/**
 * Confirmation modal component for yes/no dialogs
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    children,
    confirmButtonStyle = 'primary'
}) => {
    const theme = useTheme();
    // Most `onConfirm` handlers across the app are already async (an API call + their own busy
    // state elsewhere on the page) -- this modal used to fire onConfirm() and call onClose()
    // immediately after, in the same tick, without awaiting it. The modal vanished before the
    // request even started, with no in-flight indicator anywhere in it, and a double-click could
    // fire onConfirm twice. Now it awaits onConfirm() and only closes once it (successfully or
    // not) settles, matching the existing "always closes eventually" behavior every caller
    // already relies on, just no longer racing ahead of the work it triggered.
    const [isConfirming, setIsConfirming] = useState(false);

    const buttonStyles = {
        primary: {
            backgroundColor: theme.colors.primary,
            color: '#FFFFFF',
            border: `1px solid ${theme.colors.primary}`,
        },
        danger: {
            backgroundColor: '#dc2626',
            color: '#FFFFFF',
            border: '1px solid #dc2626',
        },
    };

    const baseButtonStyle = {
        padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
        borderRadius: theme.borderRadius.md,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.medium,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        minWidth: '120px',
    };

    const confirmButtonStyle_obj = {
        ...baseButtonStyle,
        ...buttonStyles[confirmButtonStyle],
    };

    const cancelButtonStyle = {
        ...baseButtonStyle,
        backgroundColor: 'transparent',
        color: theme.colors.text.primary,
        border: `1px solid ${theme.colors.border.default}`,
    };

    const handleConfirm = async () => {
        if (isConfirming) return; // guard against a double-click firing onConfirm twice
        setIsConfirming(true);
        try {
            await onConfirm();
        } finally {
            setIsConfirming(false);
            onClose();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            <div style={{ marginBottom: theme.spacing.lg }}>
                <p style={{
                    fontSize: theme.typography.fontSize.md,
                    color: theme.colors.text.primary,
                    lineHeight: 1.5,
                    margin: 0,
                    // A message built from more than one sentence can separate them with a blank
                    // line. Single-line messages, which is most of them, are unaffected.
                    whiteSpace: 'pre-line',
                }}>
                    {message}
                </p>
                {children}
            </div>
            <div style={{
                display: 'flex',
                gap: theme.spacing.md,
                justifyContent: 'flex-end',
            }}>
                <button
                    style={{ ...cancelButtonStyle, cursor: isConfirming ? 'not-allowed' : 'pointer', opacity: isConfirming ? 0.6 : 1 }}
                    onClick={onClose}
                    disabled={isConfirming}
                    onMouseEnter={(e) => { if (!isConfirming) e.currentTarget.style.backgroundColor = theme.colors.background; }}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                    {cancelText}
                </button>
                <button
                    style={{ ...confirmButtonStyle_obj, cursor: isConfirming ? 'not-allowed' : 'pointer', opacity: isConfirming ? 0.6 : 1 }}
                    onClick={handleConfirm}
                    disabled={isConfirming}
                    onMouseEnter={(e) => {
                        if (isConfirming) return;
                        const opacity = confirmButtonStyle === 'danger' ? 0.9 : 0.9;
                        e.currentTarget.style.opacity = opacity.toString();
                    }}
                    onMouseLeave={(e) => { if (!isConfirming) e.currentTarget.style.opacity = '1'; }}
                >
                    {isConfirming ? 'Working…' : confirmText}
                </button>
            </div>
        </Modal>
    );
};
